// src/pages/admin-accounts/AdminAccountFormPage/AdminAccountFormPage.jsx
import React, { useCallback, useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import AdminAccountsAPI from '../../../api/v3/admin-accounts-api-v3'

const BASE = '/app/sales/bagspa/admin-accounts'

const ROLE_OPTIONS = [
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'admin', label: 'Admin' },
  { value: 'sales_ops', label: 'Sales Ops' },
  { value: 'finance', label: 'Finance' }
]

const initialForm = {
  email: '',
  first_name: '',
  last_name: '',
  role: 'admin',
  password: '',
  password_confirmation: ''
}

export default function AdminAccountFormPage() {
  const { id } = useParams()
  const history = useHistory()
  const isEdit = Boolean(id)

  const [form, setForm] = useState(initialForm)
  const [roles, setRoles] = useState(ROLE_OPTIONS)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const loadRoles = useCallback(async () => {
    try {
      const json = await AdminAccountsAPI.roles()
      if (Array.isArray(json?.roles) && json.roles.length > 0) {
        setRoles(json.roles)
      }
    } catch (_) {
      setRoles(ROLE_OPTIONS)
    }
  }, [])

  const loadAdmin = useCallback(async () => {
    if (!isEdit) return

    try {
      setLoading(true)
      setError('')

      const json = await AdminAccountsAPI.show(id)
      const item = json?.item || {}

      setForm({
        email: item.email || '',
        first_name: item.first_name || '',
        last_name: item.last_name || '',
        role: item.role || 'admin',
        password: '',
        password_confirmation: ''
      })
    } catch (e) {
      setError(e.message || 'Failed to fetch admin account')
    } finally {
      setLoading(false)
    }
  }, [id, isEdit])

  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  useEffect(() => {
    loadAdmin()
  }, [loadAdmin])

  const validate = () => {
    if (!form.email.trim()) return 'Email is required.'
    if (!form.role) return 'Role is required.'

    if (!isEdit && !form.password) return 'Password is required.'

    if (form.password || form.password_confirmation) {
      if (form.password.length < 8) return 'Password minimum is 8 characters.'
      if (form.password !== form.password_confirmation) return 'Password confirmation does not match.'
    }

    return ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      setSaving(true)
      setError('')

      const payload = {
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: form.role
      }

      if (form.password) {
        payload.password = form.password
        payload.password_confirmation = form.password_confirmation
      }

      if (isEdit) {
        await AdminAccountsAPI.update(id, payload)
      } else {
        await AdminAccountsAPI.create(payload)
      }

      history.push(BASE)
    } catch (e) {
      setError(e.message || 'Failed to save admin account')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="bagspa-page admin-account-form-page">
      <div
        className="d-flex justify-content-between align-items-center mb-3"
        style={{ gap: 8, flexWrap: 'wrap' }}
      >
        <div>
          <h3 className="mb-0">
            {isEdit ? 'Edit Admin Account' : 'Create New Admin Account'}
          </h3>
          <div className="text-muted small">
            {isEdit
              ? 'Update dashboard BagSpa admin account.'
              : 'Create a new account for dashboard BagSpa.'}
          </div>
        </div>

        <Link className="btn btn-outline-secondary" to={BASE}>
          &larr; Back
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center text-muted py-4">Loading...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group mb-3">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  placeholder="admin@belleandkate.com"
                  autoComplete="off"
                />
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.first_name}
                      onChange={(e) => setField('first_name', e.target.value)}
                      placeholder="First name"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.last_name}
                      onChange={(e) => setField('last_name', e.target.value)}
                      placeholder="Last name"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>
                      Password {isEdit && <span className="text-muted">(optional)</span>}
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.password}
                      onChange={(e) => setField('password', e.target.value)}
                      placeholder={isEdit ? 'Leave blank if unchanged' : 'Minimum 8 characters'}
                      autoComplete="new-password"
                    />
                    {isEdit ? (
                      <small className="text-muted">
                        Fill this only when you want to change the password.
                      </small>
                    ) : null}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>Password Confirmation</label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.password_confirmation}
                      onChange={(e) => setField('password_confirmation', e.target.value)}
                      placeholder="Retype password"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group mb-4">
                <label>Role</label>
                <select
                  className="form-control"
                  value={form.role}
                  onChange={(e) => setField('role', e.target.value)}
                >
                  {roles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                <Link className="btn btn-outline-secondary" to={BASE}>
                  Cancel
                </Link>

                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : isEdit ? 'Update Admin' : 'Save Admin'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}